import { createPortal } from "react-dom";

const bd = "1px solid #000";
const cellBase = { border: bd, padding: "3px 8px", fontSize: "14px", textAlign: "center", whiteSpace: "nowrap", lineHeight: 1.15 };
const diaCell = { ...cellBase, fontWeight: 800, textAlign: "left", background: "#f1f5f9" };
const lenCell = { ...cellBase, fontWeight: 700, background: "#f8fafc", color: "#334155", fontSize: "12px" };
const rateCell = { ...cellBase, fontWeight: 700 };
const sectionTitle = {
  fontWeight: 900,
  fontSize: "16px",
  background: "#0f172a",
  color: "#fff",
  padding: "5px 11px",
  marginTop: "6px",
  letterSpacing: "0.04em",
};

export const fmt = (n) => (Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

// numeric value of an inch/metric length label for sorting
function parseLen(s) {
  if (s === undefined || s === null) return 0;
  s = String(s).trim();
  if (/^\d+$/.test(s)) return parseInt(s, 10);
  let whole = 0,
    frac = 0;
  if (s.includes(".")) {
    const [w, f] = s.split(".");
    whole = parseFloat(w) || 0;
    if (f && f.includes("/")) {
      const [a, b] = f.split("/");
      frac = (parseFloat(a) || 0) / (parseFloat(b) || 1);
    } else frac = parseFloat("0." + f) || 0;
  } else if (s.includes("/")) {
    const [a, b] = s.split("/");
    frac = (parseFloat(a) || 0) / (parseFloat(b) || 1);
  } else whole = parseFloat(s) || 0;
  return whole + frac;
}

// collapse consecutive equal-rate lengths into ranges
function segments(list) {
  const sorted = [...list].sort((a, b) => parseLen(a.len) - parseLen(b.len));
  const segs = [];
  for (const r of sorted) {
    const last = segs[segs.length - 1];
    if (last && last.rate === r.rate) last.end = r.len;
    else segs.push({ start: r.len, end: r.len, rate: r.rate });
  }
  return segs.map((s) => ({ label: s.start === s.end ? s.start : `${s.start} TO ${s.end}`, rate: s.rate }));
}

// group bolt rows into ordered diameter+thread rows
function boltRows(rows, cat) {
  const map = new Map();
  for (const r of rows) {
    if (r.cat !== cat) continue;
    const key = `${r.dia}|${r.thread}`;
    if (!map.has(key)) {
      const label = cat === "metric" ? `M${r.dia} ${r.thread}` : `${r.dia} ${r.thread}`;
      map.set(key, { label: label.trim(), items: [] });
    }
    map.get(key).items.push(r);
  }
  return [...map.values()].map((g) => ({ label: g.label, segs: segments(g.items) }));
}

export function BoltGrid({ title, rows, cat, perKg = false, big = false }) {
  const groups = boltRows(rows, cat);
  if (!groups.length) return null;
  const cCell = big ? { ...cellBase, fontSize: "20px", padding: "7px 12px" } : cellBase;
  const dCell = big ? { ...diaCell, fontSize: "20px", padding: "7px 12px", width: "120px" } : { ...diaCell, width: "80px" };
  const lCell = big ? { ...lenCell, fontSize: "16px", padding: "7px 12px" } : lenCell;
  const rCell = big ? { ...rateCell, fontSize: "21px", padding: "7px 12px" } : rateCell;
  const sTitle = big ? { ...sectionTitle, fontSize: "22px", padding: "8px 14px", marginTop: "10px" } : sectionTitle;
  return (
    <div style={{ breakInside: "avoid" }}>
      <div style={sTitle}>{title}</div>
      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "auto" }}>
        <tbody>
          {groups.flatMap((g, gi) => [
            <tr key={gi + "l"}>
              <td style={dCell} rowSpan={2}>
                {g.label}
              </td>
              {g.segs.map((s, i) => (
                <td key={"l" + i} style={lCell}>
                  {s.label}
                </td>
              ))}
            </tr>,
            <tr key={gi + "r"}>
              {g.segs.map((s, i) => (
                <td key={"r" + i} style={rCell}>
                  {fmt(s.rate)}{perKg ? <span style={{ fontSize: big ? "12px" : "9px", fontWeight: 600 }}> /kg</span> : null}
                </td>
              ))}
            </tr>,
          ])}
        </tbody>
      </table>
    </div>
  );
}

function NutTable({ title, rows }) {
  if (!rows.length) return null;
  // 3 columns of size|rate pairs
  const cols = 3;
  const perCol = Math.ceil(rows.length / cols);
  const chunks = [];
  for (let i = 0; i < rows.length; i += perCol) chunks.push(rows.slice(i, i + perCol));
  const maxRows = Math.max(...chunks.map((c) => c.length));
  return (
    <div style={{ breakInside: "avoid", marginTop: "8px" }}>
      <div style={sectionTitle}>{title}</div>
      <table style={{ width: "60%", borderCollapse: "collapse" }}>
        <tbody>
          {Array.from({ length: maxRows }).map((_, ri) => (
            <tr key={ri}>
              {chunks.flatMap((c, ci) => {
                const r = c[ri];
                return [
                  <td key={ci + "s"} style={{ ...cellBase, textAlign: "left", fontWeight: 700, width: "90px" }}>
                    {r ? r.sizeLabel : ""}
                  </td>,
                  <td key={ci + "r"} style={rateCell}>
                    {r ? fmt(r.rate) : ""}
                  </td>,
                ];
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BasicStrip({ basic, nutBasic }) {
  return (
    <div style={{ display: "flex", justifyContent: "center", margin: "2px 0 6px" }}>
      <div
        data-testid="rate-print-basic-strip"
        style={{ display: "inline-flex", alignItems: "center", gap: "18px", border: "2.5px solid #000", borderRadius: "6px", padding: "5px 22px", fontSize: "19px", fontWeight: 900, letterSpacing: "0.03em", WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}
      >
        <span>BOLT BASIC ₹{fmt(basic)}</span>
        <span style={{ fontWeight: 400 }}>·</span>
        <span>NUT BASIC ₹{fmt(nutBasic)}</span>
      </div>
    </div>
  );
}

export function RatePrint({ rows, basic, nutBasic, date, grades = {} }) {
  const d = date ? new Date(date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "";
  const nuts = rows.filter((r) => r.cat === "nut").map((r) => ({ ...r, sizeLabel: r.label }));
  const inchNuts = nuts.filter((r) => !/M/.test(r.name.toUpperCase().replace("NUT", "")));
  const metricNuts = nuts.filter((r) => /M/.test(r.name.toUpperCase().replace("NUT", "")));
  const g = (k) => (grades[k] ? ` — ${grades[k]}` : "");

  return createPortal(
    <div id="print-area" style={{ fontFamily: "Arial, Helvetica, sans-serif", color: "#000" }}>
      {/* ===== PAGE 1 — INCHES ===== */}
      <div style={{ padding: "9mm 8mm" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "2px solid #000", paddingBottom: "6px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
            <img src="/trb-logo.png" alt="TRB" style={{ height: "66px", width: "66px", objectFit: "contain", WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }} />
            <div style={{ fontSize: "12px", lineHeight: 1.3 }}>
              <div style={{ fontWeight: 900, fontSize: "16px" }}>TR BOLT HOUSE <span style={{ fontWeight: 400, fontSize: "11px" }}>03BQTPG2220A1ZK</span></div>
              <div>Plot No B29-2, Near Ravi Kanda Sua Road, Industrial Area C, LUDHIANA 141003</div>
              <div style={{ fontWeight: 900, fontSize: "16px", marginTop: "4px" }}>TR ENTERPRISES <span style={{ fontWeight: 400, fontSize: "11px" }}>03AARPG1533H2ZZ</span></div>
              <div>Shop No. 857, Tagore Colony, New Cycle Market, Gill Road, LUDHIANA 141003</div>
              <div style={{ marginTop: "4px" }}>Ph (O): +91 98780-91782 &nbsp;Ishan Gupta</div>
              <div>Ph (O): +91 98728-68722 &nbsp;Naveen Gupta</div>
            </div>
          </div>
          <div style={{ textAlign: "right", fontSize: "14px" }}>
            <div style={{ fontWeight: 700 }}>Date: {d}</div>
          </div>
        </div>

        <div style={{ textAlign: "center", fontSize: "23px", fontWeight: 900, letterSpacing: "0.03em", margin: "4px 0" }}>
          TR BOLT RATE LIST
        </div>
        <BasicStrip basic={basic} nutBasic={nutBasic} />

        <BoltGrid title={`HEX BOLT — LENGTH (IN INCHES)${g("inch")}`} rows={rows} cat="inch" />
        <NutTable title={`CF NUT RATE (INCHES)${g("nut")}`} rows={inchNuts} />
      </div>

      {/* ===== PAGE 2 — METRIC ===== */}
      <div style={{ breakBefore: "page", pageBreakBefore: "always", padding: "9mm 8mm" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "2px solid #000", paddingBottom: "6px", marginBottom: "4px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <img src="/trb-logo.png" alt="TRB" style={{ height: "42px", width: "42px", objectFit: "contain", WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }} />
            <div style={{ fontSize: "22px", fontWeight: 900, letterSpacing: "0.03em" }}>TR BOLT RATE LIST</div>
          </div>
          <div style={{ textAlign: "right", fontSize: "14px" }}>
            <span style={{ fontWeight: 700 }}>Date: {d}</span>
          </div>
        </div>
        <BasicStrip basic={basic} nutBasic={nutBasic} />

        <BoltGrid title={`DIAMETER — LENGTH (IN METRICS)${g("metric")}`} rows={rows} cat="metric" />
        <NutTable title={`CF NUT RATE (METRIC)${g("nut")}`} rows={metricNuts} />

        <div style={{ marginTop: "10px", fontSize: "13px", fontWeight: 700 }}>
          NOTE — These rates are not certain, it can be changed anytime without prior notice.
        </div>
      </div>
    </div>,
    document.body
  );
}
